
/*
    types.ts

    Type-only carrier tokens used in schema definitions to break the circular
    dependency between object classes (every relation references another object
    type, and those objects in turn reference this one).

    Every token resolves to `null` at runtime: it exists purely so schemas can be
    typed against the related class without importing its runtime value.

    IMPORTANT: this module must stay dependency-free at runtime. All class imports
    below are `import type` (erased on compile), so the consts initialise
    immediately when the module loads, regardless of the order in which the rest
    of the model graph is evaluated. This prevents temporal-dead-zone errors
    (e.g. "Cannot access 'PlaceType' before initialization") when an object module
    reads a token while `model.ts` is still resolving its own imports.
*/

import type { Address } from './objects/address';
import type { AskWaiter } from './objects/askwaiter';
import type { CodescanDevice } from './objects/codescan';
import type { Pinpad } from './objects/pinpad';
import type { Category } from './objects/category';
import type { CategoryDep } from './objects/categorydep';
import type { Client } from './objects/client';
import type { DeviceOption } from './objects/deviceopt';
import type { Discount } from './objects/discount';
import type { DiscountPeriod } from './objects/discountperiod';
import type { DiscountProduct } from './objects/discountproduct';
import type { DrawItem } from './objects/draw';
import type { Employee } from './objects/employee';
import type { Extra } from './objects/extra';
import type { ExtraPeriod } from './objects/extraperiod';
import type { ExtraOption } from './objects/extraoption';
import type { ExtraProduct } from './objects/extraproduct';
import type { ExtraTable } from './objects/extratable';
import type { Family } from './objects/family';
import type { FloorArea } from './objects/floorarea';
import type { FamilyPeriod } from './objects/familyperiod';
import type { FamilyProduct } from './objects/familyproduct';
import type { I18nValue } from './objects/i18nvalue';
import type { MixedPayment } from './objects/mixedpay';
import type { PinpadPay } from './objects/pinpadpay';
import type { Offer } from './objects/offer';
import type { OfferPeriod } from './objects/offerperiod';
import type { OfferOption } from './objects/offeroption';
import type { OfferProduct } from './objects/offerproduct';
import type { AccountInvoice } from './objects/accountinvoice';
import type { Audit } from './objects/audit';
import type { TillChange } from './objects/tillchange';
import type { PayAccount } from './objects/payaccount';
import type { Payment } from './objects/payment';
import type { Place } from './objects/place';
import type { Stripe } from './objects/stripe';
import type { PlaceLink } from './objects/placelink';
import type { PlaceOption } from './objects/placeopt';
import type { PosDevice } from './objects/device';
import type { PriceZone } from './objects/pricezone';
import type { PriceTime } from './objects/pricetime';
import type { ProductPrice } from './objects/productprice';
import type { OptionPrice } from './objects/optionprice';
import type { Preselect } from './objects/preselect';
import type { PreselectOpt } from './objects/preselectopt';
import type { PrintDevice } from './objects/printer';
import type { PrinterProduct } from './objects/printerproduct';
import type { PrinterProductOpt } from './objects/printerproductopt';
import type { Product } from './objects/product';
import type { ProductOpt } from './objects/productopt';
import type { QrCode } from './objects/qrcode';
import type { QrScan } from './objects/qrscan';
import type { Swinvoice } from './objects/swinvoice';
import type { Rasppi } from './objects/rasppi';
import type { RasppiConnect } from './objects/rasppiconnect';
import type { ScaleDevice } from './objects/scale';
import type { Session } from './objects/session';
import type { FCM } from './objects/fcm';
import type { Ticket } from './objects/ticket';
import type { Till } from './objects/till';
import type { TicketChange } from './objects/ticketchange';
import type { TicketData } from './objects/ticketdata';
import type { TicketDiscount } from './objects/ticketdiscount';
import type { TicketInvoice } from './objects/ticketinvoice';
import type { TicketProduct } from './objects/ticketproduct';
import type { TicketExtra } from './objects/ticketextra';
import type { TicketOffer } from './objects/ticketoffer';
import type { TicketOption } from './objects/ticketoption';
import type { Ticketbai } from './objects/ticketbai';
import type { Ticketsii } from './objects/ticketsii';
import type { Verifactu } from './objects/verifactu';
import type { User } from './objects/user';

export const AddressType = null as unknown as abstract new (...args: any[]) => Address;
export const AskWaiterType = null as unknown as abstract new (...args: any[]) => AskWaiter;
export const CodescanDeviceType = null as unknown as abstract new (...args: any[]) => CodescanDevice;
export const PinpadType = null as unknown as abstract new (...args: any[]) => Pinpad;
export const CategoryType = null as unknown as abstract new (...args: any[]) => Category;
export const CategoryDepType = null as unknown as abstract new (...args: any[]) => CategoryDep;
export const ClientType = null as unknown as abstract new (...args: any[]) => Client;
export const DeviceOptionType = null as unknown as abstract new (...args: any[]) => DeviceOption;
export const DiscountType = null as unknown as abstract new (...args: any[]) => Discount;
export const DiscountPeriodType = null as unknown as abstract new (...args: any[]) => DiscountPeriod;
export const DiscountProductType = null as unknown as abstract new (...args: any[]) => DiscountProduct;
export const DrawItemType = null as unknown as abstract new (...args: any[]) => DrawItem;
export const EmployeeType = null as unknown as abstract new (...args: any[]) => Employee;
export const ExtraType = null as unknown as abstract new (...args: any[]) => Extra;
export const ExtraPeriodType = null as unknown as abstract new (...args: any[]) => ExtraPeriod;
export const ExtraOptionType = null as unknown as abstract new (...args: any[]) => ExtraOption;
export const ExtraProductType = null as unknown as abstract new (...args: any[]) => ExtraProduct;
export const ExtraTableType = null as unknown as abstract new (...args: any[]) => ExtraTable;
export const FamilyType = null as unknown as abstract new (...args: any[]) => Family;
export const FloorAreaType = null as unknown as abstract new (...args: any[]) => FloorArea;
export const FamilyPeriodType = null as unknown as abstract new (...args: any[]) => FamilyPeriod;
export const FamilyProductType = null as unknown as abstract new (...args: any[]) => FamilyProduct;
export const I18nValueType = null as unknown as abstract new (...args: any[]) => I18nValue;
export const MixedPaymentType = null as unknown as abstract new (...args: any[]) => MixedPayment;
export const PinpadPayType = null as unknown as abstract new (...args: any[]) => PinpadPay;
export const OfferType = null as unknown as abstract new (...args: any[]) => Offer;
export const OfferPeriodType = null as unknown as abstract new (...args: any[]) => OfferPeriod;
export const OfferOptionType = null as unknown as abstract new (...args: any[]) => OfferOption;
export const OfferProductType = null as unknown as abstract new (...args: any[]) => OfferProduct;
export const AccountInvoiceType = null as unknown as abstract new (...args: any[]) => AccountInvoice;
export const AuditType = null as unknown as abstract new (...args: any[]) => Audit;
export const TillChangeType = null as unknown as abstract new (...args: any[]) => TillChange;
export const PayAccountType = null as unknown as abstract new (...args: any[]) => PayAccount;
export const PaymentType = null as unknown as abstract new (...args: any[]) => Payment;
export const PlaceType = null as unknown as abstract new (...args: any[]) => Place;
export const StripeType = null as unknown as abstract new (...args: any[]) => Stripe;
export const PlaceLinkType = null as unknown as abstract new (...args: any[]) => PlaceLink;
export const PlaceOptionType = null as unknown as abstract new (...args: any[]) => PlaceOption;
export const PosDeviceType = null as unknown as abstract new (...args: any[]) => PosDevice;
export const PriceZoneType = null as unknown as abstract new (...args: any[]) => PriceZone;
export const PriceTimeType = null as unknown as abstract new (...args: any[]) => PriceTime;
export const ProductPriceType = null as unknown as abstract new (...args: any[]) => ProductPrice;
export const PreselectType = null as unknown as abstract new (...args: any[]) => Preselect;
export const PreselectOptType = null as unknown as abstract new (...args: any[]) => PreselectOpt;
export const PrintDeviceType = null as unknown as abstract new (...args: any[]) => PrintDevice;
export const PrinterProductType = null as unknown as abstract new (...args: any[]) => PrinterProduct;
export const PrinterProductOptType = null as unknown as abstract new (...args: any[]) => PrinterProductOpt;
export const ProductType = null as unknown as abstract new (...args: any[]) => Product;
export const ProductOptType = null as unknown as abstract new (...args: any[]) => ProductOpt;
export const OptionPriceType = null as unknown as abstract new (...args: any[]) => OptionPrice;
export const QrCodeType = null as unknown as abstract new (...args: any[]) => QrCode;
export const QrScanType = null as unknown as abstract new (...args: any[]) => QrScan;
export const SwinvoiceType = null as unknown as abstract new (...args: any[]) => Swinvoice;
export const RasppiType = null as unknown as abstract new (...args: any[]) => Rasppi;
export const RasppiConnectType = null as unknown as abstract new (...args: any[]) => RasppiConnect;
export const ScaleDeviceType = null as unknown as abstract new (...args: any[]) => ScaleDevice;
export const SessionType = null as unknown as abstract new (...args: any[]) => Session;
export const FCMType = null as unknown as abstract new (...args: any[]) => FCM;
export const TicketType = null as unknown as abstract new (...args: any[]) => Ticket;
export const TillType = null as unknown as abstract new (...args: any[]) => Till;
export const TicketChangeType = null as unknown as abstract new (...args: any[]) => TicketChange;
export const TicketDataType = null as unknown as abstract new (...args: any[]) => TicketData;
export const TicketDiscountType = null as unknown as abstract new (...args: any[]) => TicketDiscount;
export const TicketInvoiceType = null as unknown as abstract new (...args: any[]) => TicketInvoice;
export const TicketProductType = null as unknown as abstract new (...args: any[]) => TicketProduct;
export const TicketExtraType = null as unknown as abstract new (...args: any[]) => TicketExtra;
export const TicketOfferType = null as unknown as abstract new (...args: any[]) => TicketOffer;
export const TicketOptionType = null as unknown as abstract new (...args: any[]) => TicketOption;
export const TicketbaiType = null as unknown as abstract new (...args: any[]) => Ticketbai;
export const TicketsiiType = null as unknown as abstract new (...args: any[]) => Ticketsii;
export const VerifactuType = null as unknown as abstract new (...args: any[]) => Verifactu;
export const UserType = null as unknown as abstract new (...args: any[]) => User;
